import { CalendarClock, Clock, Route, Timer } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Combobox } from '@/components/ui/combobox';
import { Select } from '@/components/ui/form';
import { Card, PageHeader, Spinner } from '@/components/ui/layout';
import { t, tn } from '@/lib/i18n';
import { useNetwork } from '@/lib/network';
import { type Performance, usePerformance } from '@/lib/performance';

const min = (v: number | null) => (v == null ? '—' : t('{n} min', { n: v }));
const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—');

/**
 * How a direction runs in practice, from when buses actually passed its stations: trips, travel
 * time and gaps between buses by hour, time between stops and punctuality against the timetable.
 */
export function PerformancePage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const days = Number(params.get('days')) || 14;
  const day = params.get('day') === 'weekend' ? 'weekend' : 'week';
  const network = useNetwork();
  const navigate = useNavigate();
  const perf = usePerformance(id, days, day);
  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next);
  };

  // Directions grouped by line: "1 · Linja 1 — going".
  const options =
    network.data?.zones
      .flatMap((z) =>
        [
          [z.on_line_going, t('going')],
          [z.on_line_return, t('return')],
        ]
          .filter(([lid]) => lid)
          .map(([lid, dir]) => {
            const l = network.data!.lines.find((x) => x._id === lid);
            return { value: lid!, label: `${z.bus_sign} · ${z.name} — ${dir}`, hint: l?.name };
          }),
      )
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })) ?? [];

  return (
    <>
      <PageHeader
        title={t('Performance')}
        subtitle={t('How each direction runs in practice, from when buses actually passed its stations.')}
      />
      <Card padded={false} className="mb-6">
        <div className="flex flex-wrap items-center gap-2 p-3">
          <Combobox
            className="w-96"
            value={id ?? ''}
            onChange={(v) => v && navigate(`/performance/${v}?${params.toString()}`)}
            placeholder={t('Choose a direction…')}
            options={options}
          />
          <Select
            className="w-40"
            value={days}
            onChange={(e) => setParam('days', e.target.value)}
            aria-label={t('Period')}
          >
            {[7, 14, 30, 90].map((n) => (
              <option key={n} value={n}>
                {t('Last {n} days', { n })}
              </option>
            ))}
          </Select>
          <div className="flex rounded-md bg-slate-100 p-0.5 text-sm">
            {(['week', 'weekend'] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setParam('day', d)}
                className={`rounded px-3 py-1 ${day === d ? 'bg-white font-medium shadow-sm' : 'text-slate-600'}`}
              >
                {d === 'week' ? t('Weekdays') : t('Weekend')}
              </button>
            ))}
          </div>
          {id && (
            <span className="ml-auto flex gap-3 text-sm">
              <Link to={`/directions/${id}`} className="font-medium text-brand-700 hover:underline">
                {t('Edit direction')}
              </Link>
              <Link to={`/schedules/directions/${id}`} className="font-medium text-brand-700 hover:underline">
                {t('Timetable')}
              </Link>
            </span>
          )}
        </div>
      </Card>
      {!id ? (
        <p className="text-sm text-slate-500">{t('Choose a direction to see how it runs.')}</p>
      ) : perf.isLoading || !perf.data ? (
        <Spinner />
      ) : perf.data.trips === 0 ? (
        <Card>
          <p className="text-sm text-slate-600">
            {t(
              'No trips recorded for this direction in this period yet. They are read from the bus positions every 2 minutes, so they build up from now on.',
            )}
          </p>
        </Card>
      ) : (
        <Report p={perf.data} />
      )}
    </>
  );
}

function Tile({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Icon className="size-4" /> {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function Report({ p }: { p: Performance }) {
  const pu = p.punctuality;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          icon={Route}
          label={t('Trips per day')}
          value={p.trips_per_day}
          sub={tn(p.days_with_data, '{n} day with data', '{n} days with data')}
        />
        <Tile
          icon={Timer}
          label={t('Travel time, first to last stop')}
          value={min(p.travel.median)}
          sub={p.travel.p90 != null ? t('slow trips: {n} min', { n: p.travel.p90 }) : undefined}
        />
        <Tile
          icon={Clock}
          label={t('Usual gap between buses')}
          value={min(p.headway.median)}
          sub={tn(p.headway.bunched, '{n} time two buses left together', '{n} times two buses left together')}
        />
        <Tile
          icon={CalendarClock}
          label={t('On time (timetable)')}
          value={pu.has_timetable ? pct(pu.on_time, pu.n) : '—'}
          sub={
            pu.has_timetable
              ? t('{late} late · {early} early of {n} stops', { late: pu.late, early: pu.early, n: pu.n })
              : t('No timetable for these days')
          }
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card title={t('Travel time by hour of departure')}>
          <HourChart
            rows={p.travel.by_hour}
            bar={(r) => r.median}
            tick={(r) => r.p90}
            barLabel={t('usual')}
            tickLabel={t('slow trips')}
            tip={(r) =>
              t('{h}:00 · usual {a} min · slow {b} min · {n} trips', {
                h: pad(r.h),
                a: r.median ?? '—',
                b: r.p90 ?? '—',
                n: r.n,
              })
            }
          />
        </Card>
        <Card title={t('Gap between buses by hour')}>
          <HourChart
            rows={p.headway.by_hour}
            bar={(r) => r.median}
            tick={(r) => r.p90}
            barLabel={t('usual')}
            tickLabel={t('longest gaps')}
            tip={(r) =>
              t('{h}:00 · usual {a} min · longest {b} min · bunched {c}', {
                h: pad(r.h),
                a: r.median ?? '—',
                b: r.p90 ?? '—',
                c: r.bunched,
              })
            }
          />
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card title={t('Time between stops')} padded={false}>
          <Segments p={p} />
        </Card>
        <Card title={t('Punctuality by stop')} padded={false}>
          {pu.has_timetable ? (
            <Punctuality p={p} />
          ) : (
            <p className="px-4 py-3 text-sm text-slate-500">
              {t('This direction has no timetable for these days, so there is nothing to compare with.')}{' '}
              <Link
                to={`/schedules/directions/${p.direction._id}`}
                className="font-medium text-brand-700 hover:underline"
              >
                {t('Set the timetable')}
              </Link>
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

const pad = (h: number) => String(h).padStart(2, '0');

/** Bars by hour (e.g. the usual value) with a tick (e.g. the slow 10%); hours without data empty. */
function HourChart<R extends { h: number; n: number }>({
  rows,
  bar,
  tick,
  barLabel,
  tickLabel,
  tip,
}: {
  rows: R[];
  bar: (r: R) => number | null;
  tick: (r: R) => number | null;
  barLabel: string;
  tickLabel: string;
  tip: (r: R) => string;
}) {
  const max = Math.max(1, ...rows.flatMap((r) => [bar(r) ?? 0, tick(r) ?? 0]));
  const hours = rows.filter((r) => r.n > 0).map((r) => r.h);
  if (!hours.length) return <p className="text-sm text-slate-500">—</p>;
  const shown = rows.slice(Math.max(0, Math.min(...hours) - 1), Math.min(24, Math.max(...hours) + 2));
  return (
    <div>
      <div className="mb-1.5 flex justify-between text-xs text-slate-500">
        <span>{t('minutes')}</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="inline-block size-2.5 rounded-sm bg-brand-600/80" /> {barLabel}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-0.5 w-3 bg-slate-500" /> {tickLabel}
          </span>
        </span>
      </div>
      <div className="flex h-36 items-end gap-[3px] border-b border-slate-200">
        {shown.map((r) => {
          const b = bar(r);
          const k = tick(r);
          return (
            <div key={r.h} className="group relative flex h-full flex-1 flex-col justify-end">
              {b != null && r.n > 0 && (
                <div
                  className="rounded-t-[3px] bg-brand-600/80 group-hover:bg-brand-700"
                  style={{ height: `${Math.max(3, (b / max) * 100)}%` }}
                />
              )}
              {k != null && r.n > 0 && (
                <div
                  className="absolute inset-x-0 h-0.5 bg-slate-500"
                  style={{ bottom: `${(k / max) * 100}%` }}
                />
              )}
              {r.n > 0 && (
                <div className="pointer-events-none absolute -top-9 left-1/2 z-10 hidden -translate-x-1/2 rounded bg-slate-900 px-1.5 py-0.5 text-[11px] whitespace-nowrap text-white group-hover:block">
                  {tip(r)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-0.5 flex text-[10px] text-slate-400">
        {shown.map((r) => (
          <span key={r.h} className="flex-1 text-center">
            {r.h % 2 === 0 ? pad(r.h) : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function Segments({ p }: { p: Performance }) {
  const max = Math.max(1, ...p.segments.map((s) => s.p90 ?? s.median ?? 0));
  return (
    <div className="max-h-96 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white text-left text-xs text-slate-500">
          <tr>
            <th className="px-4 py-2 font-medium">{t('From → to')}</th>
            <th className="px-2 py-2 text-right font-medium">{t('usual')}</th>
            <th className="px-2 py-2 text-right font-medium">{t('slow')}</th>
            <th className="w-1/3 px-4 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {p.segments.map((s, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: one row per pair of stops
            <tr key={i}>
              <td className="max-w-64 px-4 py-1.5">
                <span className="block truncate" title={`${s.from} → ${s.to}`}>
                  <span className="text-slate-400">{i + 1}.</span> {s.from} → {s.to}
                </span>
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{s.median ?? '—'}</td>
              <td className="px-2 py-1.5 text-right text-slate-500 tabular-nums">{s.p90 ?? '—'}</td>
              <td className="px-4 py-1.5">
                <div className="relative h-2 rounded-full bg-slate-100">
                  {s.p90 != null && (
                    <div
                      className="absolute h-2 rounded-full bg-slate-300"
                      style={{ width: `${(s.p90 / max) * 100}%` }}
                    />
                  )}
                  {s.median != null && (
                    <div
                      className="absolute h-2 rounded-full bg-brand-600/80"
                      style={{ width: `${(s.median / max) * 100}%` }}
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Punctuality({ p }: { p: Performance }) {
  const { early, late } = p.punctuality.limits;
  // Early (orange) · on time (neutral) · late (red): the deviation's direction carries the colour.
  const tone = (m: number | null) =>
    m == null
      ? 'text-slate-400'
      : m < early
        ? 'text-amber-700'
        : m > late
          ? 'text-red-700'
          : 'text-slate-700';
  const fmt = (m: number | null) =>
    m == null
      ? '—'
      : m === 0
        ? t('on time')
        : m > 0
          ? t('{n} min late', { n: m })
          : t('{n} min early', { n: -m });
  return (
    <div className="max-h-96 overflow-y-auto">
      <p className="px-4 pt-2 text-xs text-slate-500">
        {t(
          'Each passing compared with the nearest timetable time. On time: up to {e} min early or {l} min late.',
          {
            e: -early,
            l: late,
          },
        )}
      </p>
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white text-left text-xs text-slate-500">
          <tr>
            <th className="px-4 py-2 font-medium">{t('Stop')}</th>
            <th className="px-2 py-2 text-right font-medium">{t('Usually')}</th>
            <th className="px-4 py-2 text-right font-medium">{t('On time')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {p.punctuality.by_station.map((s, i) => (
            <tr key={s.station}>
              <td className="max-w-64 px-4 py-1.5">
                <span className="block truncate">
                  <span className="text-slate-400">{i + 1}.</span> {s.name}
                </span>
              </td>
              <td className={`px-2 py-1.5 text-right ${tone(s.median)}`}>{fmt(s.median)}</td>
              <td className="px-4 py-1.5 text-right tabular-nums">{pct(s.on_time, s.n)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
